import {
  EllipsisOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  SearchOutlined,
  SendOutlined,
  WarningTwoTone,
} from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { css } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { monoFont, tabularNums } from '@medcom/shared-ui/styles/globalCss.ts';
import { MIN_TOKEN_LENGTH_PG_TRGM } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Alert, App, Button, DatePicker, Dropdown, Input, Popover, Space, Table, Tooltip, Typography, theme } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import type React from 'react';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { useLaunchOnly } from '../ais/useAisCapabilities';
import { MainContent } from '../components/MainContent';
import { PrevNextPager } from '../components/PrevNextPager.tsx';
import { RequestStatusBadge } from '../components/RequestStatusBadge';
import { TableColumnSettings } from '../components/TableColumnSettings';
import type { PatientDataInput } from '../graphql/generated/graphql.ts';
import { OrderType, RequestStatus, type SentRequestsQuery, SortOrder } from '../graphql/generated/graphql.ts';
import { mayReorder, startReorder } from '../hooks/startReorder';
import { useAppStore } from '../hooks/store/useAppStore';
import type { TableColumnSetting } from '../hooks/store/useUserSettingsStore.ts';
import { useLocalExport } from '../hooks/useLocalExport.ts';
import { useRequestCopy } from '../hooks/useRequestCopy';
import { tableActionCell, tableHoverPointer } from '../styles/globalCss';
import { API_DATE_FORMAT, SHORT_DATE_FORMAT, SHORT_DATETIME_FORMAT } from '../utils/dateFormatUtils';
import { hasSomeRole, Role } from '../utils/user';
import { FormsModal } from './forms/FormsModal.tsx';
import { usePatientMenuItems } from './patientMenuItems.tsx';
import { ReorderButton } from './request/ReorderButton';
import { RequestDetailsModal } from './request/RequestDetailsModal';
import { showCopyWarnings } from './showCopyWarnings';
import { useSentRequestsList } from './useSentRequestsList.ts';

const { RangePicker } = DatePicker;

type Request = NonNullable<SentRequestsQuery['sentRequests']>['entries'][number];

/** Keeps the first order per Auftragsnummer + Labor, preserving input order. */
const dedupeOrders = (orders: Request['orders']): Request['orders'] => {
  const byNumberAndLab = new Map<string, Request['orders'][number]>();
  for (const order of orders) {
    const key = `${order.lab.identifier}|${order.number}`;
    if (!byNumberAndLab.has(key)) {
      byNumberAndLab.set(key, order);
    }
  }
  return [...byNumberAndLab.values()];
};

// fallow-ignore-next-line complexity
export const SentRequests: React.FC = () => {
  const navigate = useNavigate();
  const { notification, message } = App.useApp();
  const auth = useAuth();
  const { token } = theme.useToken();

  const redoAllowed = hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_USER, Role.ROLE_LR_LAB_ADMIN, Role.ROLE_LR_FRONT_DESK], auth.user);
  const reorderAllowed = mayReorder(auth.user);
  const launchOnly = useLaunchOnly();
  const sendLocallyGeneratedAllowed = hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_LAB_ADMIN], auth.user);
  const { sendLocallyGenerated } = useLocalExport();

  const { setRequestIdForNew, setExecuteRequestCopy } = useAppStore(
    useShallow(state => ({
      setRequestIdForNew: state.setRequestIdForNew,
      setExecuteRequestCopy: state.setExecuteRequestCopy,
    }))
  );
  const {
    data,
    entries,
    hasMore,
    loading,
    refetch,
    filter,
    setFilter,
    pageable,
    setPageable,
    searchValue,
    setSearchValue,
    debouncedSearch,
    validBackendTokens,
    droppedTokens,
  } = useSentRequestsList();
  const location = useLocation();
  // an AIS link to a sent request lands here with its details open
  const [detailsRequestId, setDetailsRequestId] = useState<string | null>(location.state?.openDetails ?? null);
  const [formsPatientData, setFormsPatientData] = useState<PatientDataInput | null>(null);
  const patientMenuItems = usePatientMenuItems(setFormsPatientData);

  useRequestCopy('new', true, null, copyData => {
    showCopyWarnings(notification, copyData, !!data?.doctor?.flipParamNames);
    navigate('/anforderung/parameterauswahl');
  });

  const handleSort = (sorter: SorterResult<Request> | SorterResult<Request>[]) => {
    setPageable({
      sortBy:
        (sorter as SorterResult<Request>).field
          ?.toString()
          .replace(',', '.')
          .replace('patientData', 'patient')
          .replace('createdAt', 'realCreatedAt') || 'sentAt',
      sortOrder: (sorter as SorterResult<Request>).order === 'descend' ? SortOrder.DESC : SortOrder.ASC,
      offset: 0,
      limit: pageable.limit,
    });
  };

  const [columnSettings, setColumnSettings] = useState<TableColumnSetting[]>([
    { id: 'status', visible: true, title: 'Status', checkable: false },
    { id: 'patientData.lastName', visible: true, title: 'Nachname', checkable: true },
    { id: 'patientData.firstName', visible: true, title: 'Vorname', checkable: true },
    { id: 'createdAt', visible: false, title: 'Erstellt am', checkable: true },
    { id: 'sentAt', visible: true, title: 'Gesendet am', checkable: false },
    { id: 'patientData.svnr', visible: true, title: 'SVNR', checkable: true },
    { id: 'patientData.birthday', visible: true, title: 'Geburtstag', checkable: true },
    { id: 'sampledAt', visible: false, title: 'Probenentnahme am', checkable: true },
    { id: 'orders', visible: true, title: 'Auftrag', checkable: true },
    { id: 'createdByLastName', visible: false, title: 'Erstellt von', checkable: true },
    { id: 'modifiedByLastName', visible: false, title: 'Bearbeitet von', checkable: true },
    { id: 'sentByLastName', visible: false, title: 'Gesendet von', checkable: true },
    { id: 'comment', visible: false, title: 'Kommentar', checkable: true },
  ]);

  const columns: ColumnsType<Request> = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      width: 100,
      ellipsis: true,
      render: value => <RequestStatusBadge status={value} />,
    },
    {
      title: 'Nachname',
      dataIndex: ['patientData', 'lastName'],
      key: 'patientData.lastName',
      sorter: true,
      width: 110,
      ellipsis: true,
      render: (_, record) => (
        <Typography.Text style={{ fontWeight: 500 }}>
          <Markable tokens={validBackendTokens}>{record.patientData.lastName}</Markable>
        </Typography.Text>
      ),
    },
    {
      title: 'Vorname',
      dataIndex: ['patientData', 'firstName'],
      key: 'patientData.firstName',
      sorter: true,
      width: 100,
      ellipsis: true,
      render: (_, record) => <Markable tokens={validBackendTokens}>{record.patientData.firstName}</Markable>,
    },
    {
      title: 'Erstellt am',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      width: 100,
      ellipsis: true,
      render: value => <span className={tabularNums}>{dayjs(value).format(SHORT_DATETIME_FORMAT)}</span>,
    },
    {
      title: 'Gesendet am',
      dataIndex: 'sentAt',
      key: 'sentAt',
      defaultSortOrder: 'descend',
      sorter: true,
      width: 100,
      ellipsis: true,
      render: value => <span className={tabularNums}>{dayjs(value).format(SHORT_DATETIME_FORMAT)}</span>,
    },
    {
      title: 'SVNR',
      dataIndex: ['patientData', 'svnr'],
      key: 'patientData.svnr',
      sorter: true,
      width: 70,
      ellipsis: true,
      render: value => (
        <span className={monoFont}>
          <Markable tokens={validBackendTokens}>{value}</Markable>
        </span>
      ),
    },
    {
      title: 'Geburtstag',
      dataIndex: ['patientData', 'birthday'],
      key: 'patientData.birthday',
      sorter: true,
      width: 120,
      ellipsis: true,
      render: value => <span className={tabularNums}>{dayjs(value).format(SHORT_DATE_FORMAT)}</span>,
    },
    {
      title: 'Probenentnahme am',
      dataIndex: 'sampledAt',
      key: 'sampledAt',
      sorter: true,
      width: 180,
      ellipsis: true,
      render: value => <span className={tabularNums}>{dayjs(value).format(SHORT_DATETIME_FORMAT)}</span>,
    },
    {
      title: 'Auftrag',
      dataIndex: 'orders',
      key: 'orders',
      width: 100,
      ellipsis: true,
      render: (_, record) => (
        <Space>
          {record.ordersTruncated && (
            <Tooltip title="Multi-Labor-Anforderung: Sie haben keine Berechtigung für alle Aufträge dieser Anforderung.">
              <WarningTwoTone twoToneColor={token.colorWarning} />
            </Tooltip>
          )}
          {dedupeOrders(
            // Nachforderungen first, so they win the dedupe for a shared Auftragsnummer
            [...record.orders].sort((a, b) => Number(b.type === OrderType.REORDER) - Number(a.type === OrderType.REORDER))
          )
            .sort((a, b) => a.lab.id.localeCompare(b.lab.id) || a.number.localeCompare(b.number) || a.createdAt.localeCompare(b.createdAt))
            .map((order, index, orders) => (
              <span key={order.id}>
                <span className={monoFont}>
                  <Markable tokens={validBackendTokens}>{order.number}</Markable>
                </span>{' '}
                {order.type === OrderType.REORDER && <PlusCircleOutlined />}{' '}
                <Typography.Text type="secondary">{order.lab.shortName}</Typography.Text>
                {orders.length - 1 > index ? ' - ' : ''}
              </span>
            ))}
        </Space>
      ),
    },
    {
      title: 'Erstellt von',
      key: 'createdByLastName',
      sorter: true,
      width: 110,
      ellipsis: true,
      render: (_, record) => `${record.createdByFirstName} ${record.createdByLastName}`,
    },
    {
      title: 'Bearbeitet von',
      key: 'modifiedByLastName',
      sorter: true,
      width: 140,
      ellipsis: true,
      render: (_, record) => `${record.modifiedByFirstName} ${record.modifiedByLastName}`,
    },
    {
      title: 'Gesendet von',
      key: 'sentByLastName',
      sorter: true,
      width: 140,
      ellipsis: true,
      // fallow-ignore-next-line code-duplication
      render: (_, record) => `${record.sentByFirstName} ${record.sentByLastName}`,
    },
    {
      title: 'Kommentar',
      dataIndex: 'comment',
      key: 'comment',
      width: 90,
      ellipsis: true,
      sorter: false,
      render: comment =>
        comment ? (
          <Popover trigger={['hover']} content={<div style={{ whiteSpace: 'pre-wrap', maxWidth: '500px' }}>{comment}</div>}>
            <Button
              type="link"
              className={css`
                height: 20px;
                margin: 0;
                padding: 0;
              `}
              icon={<EyeOutlined />}
            />
          </Popover>
        ) : null,
    },
  ];

  const actionsColumn: ColumnType<Request> = {
    title: '',
    key: 'actions',
    fixed: 'right',
    align: 'right',
    ellipsis: true,
    width: '100px',
    className: tableActionCell,
    render: (_, record) => {
      const items: ItemType[] = [];
      items.push(...patientMenuItems(record.patientData));
      if (redoAllowed && !launchOnly) {
        items.push({
          label: 'Als Basis für Neue',
          key: 'redo-request',
          icon: <RedoOutlined />,
          onClick: info => {
            info.domEvent.stopPropagation();
            setRequestIdForNew(record.id);
            setExecuteRequestCopy(true);
          },
        });
      }
      if (record.status === RequestStatus.LOCALLY_GENERATED && sendLocallyGeneratedAllowed) {
        items.push({
          label: 'Jetzt senden',
          key: 'send-locally-generated',
          icon: <SendOutlined />,
          onClick: async info => {
            info.domEvent.stopPropagation();
            try {
              await sendLocallyGenerated(record.id);
              message.success('Anforderung wurde gesendet');
              refetch();
            } catch (_error) {
              message.error('Fehler beim Senden der Anforderung');
            }
          },
        });
      }

      return (
        <>
          {record.reorderable && reorderAllowed && (
            <Tooltip title={<span>bis {dayjs(record.reorderableUntil ?? undefined).format(SHORT_DATETIME_FORMAT)}</span>} placement="left">
              <Button
                type="link"
                icon={<PlusCircleOutlined />}
                onClick={e => {
                  e.stopPropagation();
                  startReorder(record, navigate, { user: auth.user });
                }}
              >
                Nachforderung
              </Button>
            </Tooltip>
          )}
          <Dropdown menu={{ items: items }} trigger={['click']} placement="bottomRight">
            <Button
              icon={<EllipsisOutlined style={{ fontSize: '20px', verticalAlign: 'middle' }} />}
              type="text"
              onClick={e => e.stopPropagation()}
            />
          </Dropdown>
        </>
      );
    },
  };

  const filteredColumns: ColumnsType<Request> = [
    ...columnSettings
      .filter(cs => cs.visible)
      .map(cs => {
        return columns.find(c => c.key === cs.id)!;
      }),
    actionsColumn,
  ];

  return (
    <MainContent>
      <PageHeader
        title="Gesendete Anforderungen"
        style={{ padding: 0, paddingBottom: 'inherit' }}
        extra={
          <Space key="searchbar" orientation="horizontal">
            <RangePicker
              allowEmpty={[true, true]}
              allowClear
              format={SHORT_DATE_FORMAT}
              presets={[
                { label: 'Heute', value: [dayjs(), dayjs()] },
                { label: 'Gestern', value: [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')] },
                { label: 'Letzte 7 Tage', value: [dayjs().subtract(7, 'days'), dayjs()] },
                { label: 'Letzte 28 Tage', value: [dayjs().subtract(28, 'days'), dayjs()] },
                { label: 'Diese Woche', value: [dayjs().startOf('week'), dayjs()] },
                { label: 'Dieser Monat', value: [dayjs().startOf('month'), dayjs()] },
                { label: 'Dieses Jahr', value: [dayjs().startOf('year'), dayjs()] },
                { label: 'Gesamtzeit', value: [null, null] },
              ]}
              disabledDate={current => current.isAfter(dayjs())}
              onChange={dates =>
                setFilter({
                  ...filter,
                  from: dates?.[0] ? dates[0].format(API_DATE_FORMAT) : null,
                  to: dates?.[1] ? dates[1].format(API_DATE_FORMAT) : null,
                })
              }
            />
            <Input
              autoFocus
              allowClear
              placeholder="Suche"
              value={searchValue ?? ''}
              onChange={e => {
                setSearchValue(e.target.value);
                debouncedSearch(e.target.value);
              }}
              prefix={<SearchOutlined />}
              suffix={
                <Tooltip title="Suche nach Vorname, Nachname, Auftragsnummer oder SVNR">
                  <InfoCircleOutlined />
                </Tooltip>
              }
              style={{ width: '300px' }}
            />
            <DatePicker
              format={SHORT_DATE_FORMAT}
              placeholder="Geburtstag"
              allowClear
              onChange={date =>
                setFilter({
                  ...filter,
                  birthday: date ? date.format(API_DATE_FORMAT) : null,
                })
              }
              disabledDate={current => current && (current.isAfter(dayjs()) || current.isBefore(dayjs().subtract(120, 'years')))}
            />
            <TableColumnSettings tableKey="sentRequests" initialColumnSettings={columnSettings} onChange={setColumnSettings} />
          </Space>
        }
      />
      {droppedTokens.length > 0 && (
        <Alert
          showIcon
          type="warning"
          title={`Folgende Suchbegriffe sind zu kurz und werden ignoriert: ${droppedTokens.join(', ')} (mindestens ${MIN_TOKEN_LENGTH_PG_TRGM} Zeichen pro Wort)`}
          style={{ marginBottom: 8 }}
        />
      )}
      <Table<Request>
        scroll={{ x: 'max-content' }}
        sticky
        showSorterTooltip={false}
        rowKey={record => record.id}
        size="middle"
        dataSource={entries}
        pagination={false}
        onChange={(_, __, sorter) => handleSort(sorter)}
        loading={loading}
        columns={filteredColumns}
        onRow={record => ({
          onClick: () => {
            setDetailsRequestId(record.id);
          },
        })}
        rowClassName={tableHoverPointer}
      />
      <PrevNextPager
        offset={pageable.offset}
        pageSize={pageable.limit}
        shown={entries.length}
        hasMore={hasMore}
        loading={loading}
        label="Gesendete Anforderungen"
        onChange={(offset, limit) => setPageable({ ...pageable, offset, limit })}
      />
      <RequestDetailsModal
        requestId={detailsRequestId}
        onClose={() => setDetailsRequestId(null)}
        footerExtra={<ReorderButton requestId={detailsRequestId} />}
      />
      <FormsModal patientData={formsPatientData} onClose={() => setFormsPatientData(null)} />
    </MainContent>
  );
};
